import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { CompaniesGridWebComponent } from '../component/CompaniesGridWebComponent';
import { CompaniesGridView } from '../view/CompaniesGridView';
import { templateCompanies } from '../view/template';
import { CompanyTypeData } from '../../../company/types/company-type-data';
import {
  OnAddCompany,
  OnActivateCompany,
  OnDeleteCompany,
  OnSuspendCompany,
  OnModifyMspInProductTrialsRights,
  OnModifyMspInProductTrialsVisibility
} from '../events/events';
import { FilterEvents, GridEvents } from '../../../grid/types/common';
import { ButtonEvents, GzButtonState } from '../../../ui/gzButton/types';
import { Company } from '../types/companies-grid-company';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { CompaniesItemsActions } from '../types/companies-items-actions';
import { CompaniesGridDataProvider } from '../dataProvider/CompaniesGridDataProvider';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { CompanyType } from '../../../company/types/company-type';
import { MspInProductTrialId } from "../../../company/common/mspInProductTrialsTab/types/msp-in-product-trials-types";

export class CompaniesGridController extends BaseWebComponentController {
  public component: CompaniesGridWebComponent;
  public view: CompaniesGridView;
  public viewConstructor: typeof CompaniesGridView;

  private selectedItemsGridData: Company[];

  constructor(component: CompaniesGridWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: CompaniesGridView,
      viewTemplate: templateCompanies,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$addCompanyButton,
        event: ButtonEvents.CLICK,
        callback: this.emitAddCompanyEvent,
      },
      {
        element: this.view.dom.$deleteCompanyButton,
        event: ButtonEvents.CLICK,
        callback: this.emitDeleteCompanyEvent,
      },
      {
        element: this.view.dom.companyActions.$companyActionsButton,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.onCompanyActionSelected,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.changeGridHeaderActionsAvailability,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: this.exportCsv,
      },
      {
        element: this.view.dom.$grid,
        event: FilterEvents.TOGGLE_FILTERS_VISIBILITY,
        callback: this.onEditFiltersVisibility,
      },
    ];
  }

  async getDataForView(): Promise<{ companyTypes: CompanyTypeData[] }> {
    return {
      companyTypes: this.component.companyTypes,
    };
  }

  private emitAddCompanyEvent() {
    this.view.dom.$grid.dispatchEvent(new OnAddCompany());
  }

  private emitDeleteCompanyEvent() {
    const selectedCompanyIds = this.selectedItemsGridData.map(selectedItemGridData => {
      return { _id: selectedItemGridData.companyId };
    });

    this.view.dom.$grid.dispatchEvent(new OnDeleteCompany(selectedCompanyIds));
  }

  private onCompanyActionSelected(event: WebComponentEvent) {
    switch (event.detail.selected) {
      case CompaniesItemsActions.ActivateCompany:
        return this.emitActivateCompanyEvent();
      case CompaniesItemsActions.SuspendCompany:
        return this.emitSuspendCompanyEvent();
      case CompaniesItemsActions.ImportCompanyFromConnectWise:
        return this.showImportCompaniesFromConnectWiseWindow();
      case CompaniesItemsActions.RevokeMSPInProductTrialsForMdr:
        return this.emitModifyMspInProductTrialsRightsEvent(false, MspInProductTrialId.MdrTrial);
      case CompaniesItemsActions.AllowMSPInProductTrialsForMdr:
        return this.emitModifyMspInProductTrialsRightsEvent(true, MspInProductTrialId.MdrTrial);
      case CompaniesItemsActions.RevokeMSPInProductTrialsForEdr:
        return this.emitModifyMspInProductTrialsRightsEvent(false, MspInProductTrialId.EdrTrial);
      case CompaniesItemsActions.AllowMSPInProductTrialsForEdr:
        return this.emitModifyMspInProductTrialsRightsEvent(true, MspInProductTrialId.EdrTrial);
      case CompaniesItemsActions.RevokeMSPInProductTrialsVisibilityForMdr:
        return this.emitModifyMspInProductTrialsVisibilityEvent(false, MspInProductTrialId.MdrTrial);
      case CompaniesItemsActions.AllowMSPInProductTrialsVisibilityForMdr:
        return this.emitModifyMspInProductTrialsVisibilityEvent(true, MspInProductTrialId.MdrTrial);
      case CompaniesItemsActions.RevokeMSPInProductTrialsVisibilityForEdr:
        return this.emitModifyMspInProductTrialsVisibilityEvent(false, MspInProductTrialId.EdrTrial);
      case CompaniesItemsActions.AllowMSPInProductTrialsVisibilityForEdr:
        return this.emitModifyMspInProductTrialsVisibilityEvent(true, MspInProductTrialId.EdrTrial);
    }
  }

  private emitActivateCompanyEvent() {
    const hasSuspendedCompany = this.selectedItemsGridData.some(companyData => {
      return companyData.isSuspended;
    });

    // no suspended companies, nothing to activate
    if (!hasSuspendedCompany) {
      return;
    }

    this.view.dom.$grid.dispatchEvent(new OnActivateCompany(this.selectedItemsGridData));
  }

  private emitSuspendCompanyEvent() {
    const hasActiveCompany = this.selectedItemsGridData.some(companyData => {
      return !companyData.isSuspended;
    });

    // no active company, nothing to suspend
    if (!hasActiveCompany) {
      return;
    }

    this.view.dom.$grid.dispatchEvent(new OnSuspendCompany(this.selectedItemsGridData));
  }

  private emitModifyMspInProductTrialsRightsEvent(status: boolean, productTrialId: MspInProductTrialId): void {
    this.view.dom.$grid.dispatchEvent(new OnModifyMspInProductTrialsRights(this.selectedItemsGridData, status, productTrialId));
  }

  private emitModifyMspInProductTrialsVisibilityEvent(status: boolean, productTrialId: MspInProductTrialId): void {
    this.view.dom.$grid.dispatchEvent(new OnModifyMspInProductTrialsVisibility(this.selectedItemsGridData, status, productTrialId));
  }

  private showImportCompaniesFromConnectWiseWindow() {
    this.dependencies.ShowImportCompaniesFromConnectWiseWindow(this.view.dom.$grid);
  }

  private changeGridHeaderActionsAvailability(event: WebComponentEvent) {
    this.selectedItemsGridData = event.detail.selectionStoreData;

    this.updateDeleteBtnAvailability();
    this.updateActivateBtnAvailability();
    this.updateSuspendBtnAvailability();
    this.updateAllowMSPInProductTrialsForMdrBtnAvailability();
    this.updateRevokeMSPInProductTrialsForMdrBtnAvailability();
    this.updateAllowMspInProductTrialsForEdrBtnAvailability();
    this.updateRevokeMSPInProductTrialsForEdrBtnAvailability();
    this.updateAllowMSPInProductTrialsVisibilityForMdrBtnAvailability();
    this.updateRevokeMSPInProductTrialsVisibilityForMdrBtnAvailability();
    this.updateAllowMspInProductTrialsVisibilityForEdrBtnAvailability();
    this.updateRevokeMSPInProductTrialsVisibilityForEdrBtnAvailability();
  }

  private updateDeleteBtnAvailability() {
    // no selected items -> delete button should be disabled
    this.view.dom.$deleteCompanyButton.disabled = !(
      this.selectedItemsGridData && this.selectedItemsGridData.length > 0
    );
  }

  private updateActivateBtnAvailability() {
    const anySuspendedCompany = this.selectedItemsGridData.find(companyData => {
      return companyData.isSuspended;
    });

    this.view.$activateCompanyAction.disabled = anySuspendedCompany === undefined;
  }

  private updateSuspendBtnAvailability() {
    const anyActiveCompany = this.selectedItemsGridData.find(companyData => {
      return !companyData.isSuspended;
    });

    this.view.$suspendCompanyAction.disabled = anyActiveCompany === undefined;
  }

  private calculateMspInProductTrialsBtnAvailability(lastSelectedElement: Company): boolean {
    const [monthlyLicense] = lastSelectedElement?.licenses ?? [];
    if (!monthlyLicense) {
      return true;
    }
    return this.selectedItemsGridData.length !== 1
           || !(monthlyLicense.isMonthly && !monthlyLicense.hasMspTrial && !monthlyLicense.hasBasicMonthly)
           || lastSelectedElement.companyType !== CompanyType.Partner;
  }

  private updateRevokeMSPInProductTrialsForMdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$revokeMspInProductTrialsForMdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || !lastSelectedElement.mdrMspInProductTrialDelegate;
  }

  private updateAllowMSPInProductTrialsForMdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$allowMspInProductTrialsForMdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || lastSelectedElement.mdrMspInProductTrialDelegate;
  }

  private updateAllowMspInProductTrialsForEdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$allowMspInProductTrialsForEdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || lastSelectedElement.edrMspInProductTrialDelegate;
  }

  private updateRevokeMSPInProductTrialsForEdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$revokeMspInProductTrialsForEdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || !lastSelectedElement.edrMspInProductTrialDelegate;
  }

  private updateRevokeMSPInProductTrialsVisibilityForMdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$revokeMspInProductTrialsVisibilityForMdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || !lastSelectedElement.mdrMspInProductTrialVisible;
  }

  private updateAllowMSPInProductTrialsVisibilityForMdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$allowMspInProductTrialsVisibilityForMdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || lastSelectedElement.mdrMspInProductTrialVisible;
  }

  private updateAllowMspInProductTrialsVisibilityForEdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$allowMspInProductTrialsVisibilityForEdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || lastSelectedElement.edrMspInProductTrialVisible;
  }

  private updateRevokeMSPInProductTrialsVisibilityForEdrBtnAvailability(): void {
    const lastSelectedElement = this.selectedItemsGridData[this.selectedItemsGridData.length - 1];
    this.view.$revokeMspInProductTrialsVisibilityForEdr.disabled = this.calculateMspInProductTrialsBtnAvailability(lastSelectedElement) || !lastSelectedElement.edrMspInProductTrialVisible;
  }

  private async exportCsv(event: WebComponentEvent) {
    const companiesGridDataProvider = this.view.dom.$grid.dataProvider as CompaniesGridDataProvider;

    try {
      this.view.dom.$grid.controller?.updateCsvButtonState(
        GzButtonState.IS_LOADING,
        this.dependencies.translateFn('Change the current filter selection to generate a new export.')
      );

      const filters = companiesGridDataProvider.getConvertedFilters(event.detail.filters);

      //Start downloading the csv
      await this.dependencies.exportCompanyRecordsAsCsv(filters);

      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Started companies export.'),
        severity: ToastSeverity.SUCCESS,
      });
    } catch (error) {
      GzToastNotificationWebComponent.buildToastWrapperElement({
        message: this.dependencies.translateFn('Failed companies export.'),
        severity: ToastSeverity.ERROR,
      });
    }
  }

  private onEditFiltersVisibility(event: WebComponentEvent) {
    (this.view.dom.$grid.dataProvider as CompaniesGridDataProvider).updateFiltersVisibility(event.detail);
  }
}
